"use client";
import {
  AutocompleteGameCard,
  AutocompleteGameCardsRequest,
  AutocompleteGameCardsResponse,
} from "modules/cards/model/card";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { QUERIES } from "modules/common/query";
import { keepPreviousDataScopedTo } from "modules/common/react-query-configs";
import { useRef } from "react";
import { useInfiniteScroll } from "modules/common/hooks/useInfiniteScroll";
import {
  usePricingRequest,
  withPricingCacheKey,
} from "modules/common/hooks/usePricingRequest";
import { buildCardAutocompleteUrl } from "modules/cards/utils/cardReadUrls";
import { holdsAutocompleteRows } from "modules/cards/utils/holdsAutocompleteRows";

export const useCardAutocomplete = (req: AutocompleteGameCardsRequest) => {
  const fetch = useFetchWrapper();
  const { pricing, cacheKey, isAuthReady } = usePricingRequest();
  const scrollContainerRef = useRef<HTMLElement>(null);
  // Set while the box sits under three characters, cleared only once a key
  // has REAL data again. TanStack offers the last query WITH data, and until a
  // post-gap key resolves that is still the pre-gap search — so a flag that
  // only remembered "the previous key was under three characters" let the old
  // rows back in on the second key typed while the first was still fetching.
  // Written during render, but idempotent in BOTH directions: the set needs no
  // argument, and the reset below cannot fire for the props that would make it
  // wrong — under three characters the query is disabled and the hold refuses a
  // placeholder, so `data` is undefined and the reset is unreachable. A render
  // that does reach it has seen real, non-placeholder data for its own props,
  // which is exactly what "not cold" means, so a discarded render recomputes
  // the same answer from the same inputs.
  const wentColdRef = useRef(false);
  if (req.string.length < 3) wentColdRef.current = true;

  const infQuery = useInfiniteQuery<AutocompleteGameCardsResponse, Error>({
    queryKey: withPricingCacheKey(
      [QUERIES.MAGIC_CARDS_AUTOCOMPLETE, req],
      cacheKey
    ),
    queryFn: ({ pageParam }) => {
      if (!pricing) {
        throw new Error("Pricing is required for card autocomplete");
      }
      return fetch.publicGet(
        buildCardAutocompleteUrl({
          ...req,
          page: pageParam as number,
          ...pricing,
        }),
        null
      );
    },
    initialPageParam: 1,
    // Hold the previous results while a refined search fetches, never for a
    // different name or after the box dropped under three characters — the
    // rule and its reasons live in holdsAutocompleteRows. The request sits at
    // index 1: `[QUERY, req, pricingKey?]`.
    placeholderData: keepPreviousDataScopedTo<AutocompleteGameCardsRequest>(
      (previousReq, previousKey) =>
        // The pricing key is index 2, NOT part of `req`, so holdsAutocompleteRows
        // cannot see it: a market or currency change rewrites every price under
        // an unchanged request, and the hold would keep the old currency's rows
        // on screen with isPlaceholderData suppressing the pending state. Same
        // class as the setCode/types hold that filterShape closes. Compared
        // structurally because pricingCacheKey builds a fresh value per render.
        JSON.stringify(previousKey?.[2]) === JSON.stringify(cacheKey) &&
        holdsAutocompleteRows(previousReq, req, wentColdRef.current),
      1
    ),
    enabled: isAuthReady && !!pricing && req.string.length >= 3,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) {
        return undefined;
      }
      return allPages.length + 1;
    },
  });
  if (infQuery.data !== undefined && !infQuery.isPlaceholderData) {
    wentColdRef.current = false;
  }

  const { ref: loadMoreRef } = useInfiniteScroll({
    fetchNextPage: infQuery.fetchNextPage,
    hasNextPage: infQuery.hasNextPage,
    rootRef: scrollContainerRef,
    enabled: isAuthReady && !!pricing && req.string.length >= 3,
  });

  return {
    query: infQuery,
    loadMoreRef,
    scrollContainerRef,
    opts,
  };

  function opts(): AutocompleteGameCard[] {
    if (infQuery.data) {
      const res = infQuery.data.pages.flatMap((c) => c.items ?? []);
      if (!res) {
        return [];
      }
      return res.filter(Boolean);
    }
    return [];
  }
};
